/**
 * Text-stream animation engine for the AI Companion (CONEK-3361).
 *
 * Sole consumer: the `ai--text-stream` Stimulus controller
 * (app/javascript/controllers/ai/text_stream_controller.js).
 *
 * This started as one engine with two consumers — the React `useTextStream`
 * hook and the Stimulus controller — because both surfaces rendered the same
 * ActionCable stream and would visually drift if each ran its own
 * setInterval-based render. The React companion was deleted in the
 * React→Hotwire cutover (GA programme F2.5), so only the Stimulus consumer
 * remains, so the file moved out of the React tree (`app/frontend/shared/lib/`)
 * and into `app/javascript/lib/` next to its only importers. It is still plain
 * framework-agnostic TypeScript — nothing here knows about Stimulus — but
 * living under `@shared/` with zero React consumers made its ownership read
 * backwards, and made it look like React code nobody had cleaned up.
 *
 * Pacing model:
 *   - Animation pace is recomputed every frame as
 *       max(MIN_CHAR_DURATION_MS, elapsed_ms / chars_seen_so_far)
 *     so the engine catches up when the server is fast and stretches out
 *     when the server is slow — never lagging behind.
 *   - Hard cap (`skipAnimationThreshold`, default 1500) flushes the buffer
 *     instantly past that many characters. Beyond ~1500 chars an animated
 *     reveal is annoying, not useful.
 *   - `prefers-reduced-motion: reduce` short-circuits the rAF loop and
 *     dumps every chunk synchronously on push.
 *
 * Implementation uses `requestAnimationFrame` rather than `setInterval`:
 *   - Smooth 60fps when the tab is visible.
 *   - Browsers pause rAF when the tab is backgrounded → CPU saved without
 *     us having to track Page Visibility ourselves.
 */

const MIN_CHAR_DURATION_MS = 15
const DEFAULT_SKIP_THRESHOLD = 1500

type RenderCallback = (rendered: string) => void

export interface TextStreamOptions {
  /**
   * Where to render. Either:
   *   - HTMLElement → the engine writes the rendered string to `textContent`
   *     and toggles a `is-streaming` class on start/complete.
   *   - Callback → called with the cumulative rendered string after each tick.
   *     Use this from React (`setState`).
   */
  target: HTMLElement | RenderCallback

  /** Minimum ms per character. Default 15ms (≈ 66 chars/s). */
  minCharDurationMs?: number

  /** Skip animation past this many rendered chars. Default 1500. */
  skipAnimationThreshold?: number

  /** Fired once when the first chunk arrives. */
  onStart?: () => void

  /** Fired once when the buffer drains AND `finish()` has been called. */
  onComplete?: () => void
}

export interface TextStreamHandle {
  /** Push a chunk of text. Engine schedules its render. */
  pushChunk: (chunk: string) => void

  /** Signal that the server has stopped streaming. Drains the buffer. */
  finish: () => void

  /** Stop everything immediately. Buffer is cleared; rendered text is NOT. */
  cancel: () => void
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function isCallback(t: HTMLElement | RenderCallback): t is RenderCallback {
  return typeof t === "function"
}

function writeTo(target: HTMLElement | RenderCallback, value: string): void {
  if (isCallback(target)) {
    target(value)
  } else {
    target.textContent = value
  }
}

export function createTextStream(options: TextStreamOptions): TextStreamHandle {
  const minCharDurationMs = options.minCharDurationMs ?? MIN_CHAR_DURATION_MS
  const skipThreshold = options.skipAnimationThreshold ?? DEFAULT_SKIP_THRESHOLD
  const reducedMotion = prefersReducedMotion()

  // Buffer is an array of code points (NOT chars). `"😀".length === 2` but
  // `[..."😀"].length === 1`. We render one code point per "char tick" to
  // avoid splitting a surrogate pair across two frames.
  let buffer: string[] = []
  let rendered = ""
  let started = false
  let finished = false
  let cancelled = false
  let rafId: number | null = null
  let firstChunkTs = 0
  let totalCharsSeen = 0

  function emitStart(): void {
    if (started) return
    started = true
    options.onStart?.()
  }

  function emitComplete(): void {
    if (!finished || buffer.length > 0) return
    options.onComplete?.()
  }

  function flushAll(): void {
    if (buffer.length === 0) return
    rendered += buffer.join("")
    buffer = []
    writeTo(options.target, rendered)
  }

  function tick(now: number): void {
    if (cancelled) return
    if (buffer.length === 0) {
      rafId = null
      if (finished) emitComplete()
      return
    }

    const elapsed = Math.max(1, now - firstChunkTs)
    // `minCharDurationMs` is the MIN time per char, i.e. the MAX rate of
    // 1 char every `minCharDurationMs` ms (visual cap so the engine never
    // looks like an instantaneous dump). Server pace is N chars/elapsed ms.
    // We pick the SLOWER of the two so we never render faster than the
    // floor allows, but we also never lag behind the server: if the server
    // is slow (rate < cap), we follow it; if the server is fast, we run at
    // the cap and queue the rest.
    const maxRate = 1 / minCharDurationMs // chars per ms (visual cap)
    const serverRate = totalCharsSeen / elapsed
    const clampedRate = Math.min(maxRate, serverRate)
    // Chars to emit this frame ≈ rate × frame_budget (~16.7ms @ 60fps).
    // Always emit at least one char per frame to avoid stalling visually.
    let charsThisFrame = Math.max(1, Math.floor(clampedRate * 16))
    if (charsThisFrame > buffer.length) charsThisFrame = buffer.length

    const slice = buffer.splice(0, charsThisFrame)
    rendered += slice.join("")
    writeTo(options.target, rendered)

    if (rendered.length > skipThreshold) {
      flushAll()
    }

    if (cancelled) return
    if (buffer.length > 0) {
      rafId = requestAnimationFrame(tick)
    } else {
      rafId = null
      if (finished) emitComplete()
    }
  }

  function pushChunk(chunk: string): void {
    if (cancelled || !chunk) return

    emitStart()
    if (firstChunkTs === 0) firstChunkTs = performance.now()

    const codePoints = [...chunk]
    totalCharsSeen += codePoints.length

    // Three fast-paths flush instantly:
    //   1) Reduced-motion preference.
    //   2) Past the skip threshold (already a wall of text).
    //   3) Already finished (server done, just dumping remainder).
    if (reducedMotion || rendered.length > skipThreshold || finished) {
      rendered += codePoints.join("")
      writeTo(options.target, rendered)
      return
    }

    buffer.push(...codePoints)
    rafId ??= requestAnimationFrame(tick)
  }

  function finish(): void {
    if (finished) return
    finished = true

    if (cancelled) return

    // If the buffer is already empty (e.g. server short-replied and we
    // already drained), fire complete now.
    if (buffer.length === 0) {
      emitComplete()
      return
    }

    // Otherwise, the running rAF loop will fire complete when it drains.
    // For reduced-motion / threshold paths, flush right now so the user
    // does not stare at an unfinished line.
    if (reducedMotion || rendered.length > skipThreshold) {
      flushAll()
      emitComplete()
    }
  }

  function cancel(): void {
    cancelled = true
    buffer = []
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  return { pushChunk, finish, cancel }
}
